import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { validateEmail } from "@/lib/email";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Copy } from "lucide-react";
import { Link } from "react-router-dom";

export default function MemberNew() {
  const [form, setForm] = useState({ full_name: "", preferred_name: "", email: "", home_chapter_id: "", is_founding_member: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const { data: chapters = [] } = useQuery({ queryKey: ["chapters"], queryFn: () => api.get("/chapters") });
  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "email") setEmailError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailErr = validateEmail(form.email);
    if (emailErr) { setEmailError(emailErr); return; }
    if (!form.home_chapter_id) { toast.error("Please select a chapter"); return; }
    setLoading(true);
    try {
      const data = await api.post("/admin/members", form);
      setResult(data);
      toast.success(`${data.member_code} created \u2014 invitation sent`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    if (result?.claim_link) { navigator.clipboard.writeText(result.claim_link); toast.success("Claim link copied"); }
  };

  if (result) return (
    <div data-testid="member-new-success" className="max-w-lg">
      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-6">Member Created</h1>
      <div className="bg-walnut border border-lv-border p-5 sm:p-6 space-y-4">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Member ID</p>
          <p className="font-mono text-lg text-cream" data-testid="new-member-code">{result.member_code}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Name</p>
          <p className="text-cream">{result.full_name}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Status</p>
          <p className="text-bronze uppercase tracking-wider text-sm">{result.status}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-lv-muted">Invitation Link</p>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-xs text-cream bg-walnut-deep px-2 py-1.5 border border-lv-border break-all flex-1 select-all" data-testid="new-member-claim-link">{result.claim_link}</code>
            <button onClick={copyLink} className="text-lv-muted hover:text-bronze transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0" data-testid="copy-claim-link" aria-label="Copy link">
              <Copy size={16} />
            </button>
          </div>
        </div>
      </div>
      <div className="flex gap-3 mt-6">
        <Link to="/admin/members" className="lv-btn-secondary flex-1 text-center min-h-[44px] inline-flex items-center justify-center" data-testid="back-to-members">Back to Members</Link>
        <button onClick={() => { setResult(null); setForm({ full_name: "", preferred_name: "", email: "", home_chapter_id: "", is_founding_member: false }); }} className="lv-btn-primary flex-1 min-h-[44px]" data-testid="create-another">Create Another</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-lg" data-testid="member-new-page">
      <Link to="/admin/members" className="inline-flex items-center gap-2 text-sm text-lv-muted hover:text-cream transition-colors mb-6 min-h-[44px]">
        <ArrowLeft size={16} /> Back to Members
      </Link>
      <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide mb-8">New Member</h1>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="m-name">Full Name *</label>
          <input id="m-name" autoComplete="name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="lv-input" required data-testid="member-full-name" />
        </div>
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="m-pref">Preferred Name</label>
          <input id="m-pref" value={form.preferred_name} onChange={(e) => update("preferred_name", e.target.value)} className="lv-input" data-testid="member-preferred-name" />
        </div>
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2" htmlFor="m-email">Email *</label>
          <input id="m-email" type="text" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={`lv-input${emailError ? " border-red-500/70" : ""}`} required data-testid="member-email" />
          {emailError && <p className="text-xs text-red-400 mt-1.5 leading-relaxed" data-testid="member-email-error">{emailError}</p>}
        </div>
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase text-lv-muted mb-2">Home Chapter *</label>
          <Select value={form.home_chapter_id} onValueChange={(v) => update("home_chapter_id", v)}>
            <SelectTrigger className="bg-walnut-deep border-lv-border text-cream min-h-[48px]" data-testid="member-chapter-select">
              <SelectValue placeholder="Select chapter" />
            </SelectTrigger>
            <SelectContent className="bg-walnut-deep border-lv-border">
              {chapters.map((ch) => (
                <SelectItem key={ch.id} value={ch.id} className="text-cream hover:bg-walnut focus:bg-walnut focus:text-cream">
                  {ch.name} &mdash; {ch.city}, {ch.country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 min-h-[44px]">
          <input type="checkbox" id="founding" checked={form.is_founding_member} onChange={(e) => update("is_founding_member", e.target.checked)} className="w-5 h-5 accent-bronze" data-testid="member-founding-checkbox" />
          <label htmlFor="founding" className="text-sm text-cream tracking-wide cursor-pointer">Founding Member</label>
        </div>
        <button type="submit" disabled={loading} className="lv-btn-primary w-full min-h-[48px]" data-testid="member-create-submit">
          {loading ? "Creating\u2026" : "Create & Send Invitation"}
        </button>
      </form>
    </div>
  );
}
